import { FunctionComponent } from "react";
import { useAppStore } from "../../stores/application/useAppStore";
import { useAppHandlers } from "../../services/actions/app/useAppHandlers";

import StyledModal from "./styledModals/StyledModal";
import ConfirmationModal from "./modals/ConfirmationModal";
import DestructiveModal from "./modals/DestructiveModal";
import ModalSample from "./modals/ModalSample";
import MessageModal from "./modals/MessageModal";

import CancelFlow from "./modals/cancelation-flow/CancelFlow";
import PaymentConfirmedModal from "./modals/billing/PaymentConfirmedModal";
import PauseFlow from "./modals/cancelation-flow/PauseFlow";
import CustomColorModal from "../../fragments/modals/CustomColorModal";
import SyncConnectionsModal from "./modals/extension/SyncConnectionsModal";
import DisconnectConnectionsModal from "./modals/extension/DisconnectConnectionsModal";
import ExportConnectionsModal from "./modals/extension/ExportConnectionsModal";
import ExportToHubspotModal from "./modals/extension/ExportToHubspotModal";
import ConnectionsOverviewModal from "../../fragments/ConnectionsOverviewModal";
import CsvOverviewModal from "../../fragments/CsvOverviewModal";
import ScheduleDisconnectModal from "./modals/extension/ScheduleDisconnectModal";
import RemovalOverviewModal from "../../fragments/RemovalOverviewModal";
import AddMembersModal from "../../fragments/AddMembersModal";
import CompanyOverviewModal from "../../fragments/CompanyOverviewModal";
import CalculateEnrichmentModal from "./modals/extension/CalculateEnrichmentModal";
import useMediaQuery from "../useMediaQuery";
import EnrichmentCompleteModal from "./modals/extension/EnrichmentCompleteModal";
import BulkEditTagsModal, { isBulkEditTagsBackdropCloseBlocked } from "./modals/BulkEditTagsModal";
import CreateFeedModal from "./modals/CreateFeedModal";
import CreateFeedGuideModal from "./modals/CreateFeedGuideModal";
import RenameFeedModal from "./modals/RenameFeedModal";
import ChooseTemplateModal from "./modals/ChooseTemplateModal";
import CreateCustomFieldModal from "./modals/CreateCustomFieldModal";
import HubspotExportResultModal from "./modals/extension/HubspotExportResultModal";
import NotFoundUrlsModal from "./modals/NotFoundUrlsModal";
import EditKanbanModal from "./modals/EditKanbanModal";
import AddToKanbanModal from "./modals/AddToKanbanModal";
import AddToCampaignModal from "./modals/AddToCampaignModal";
import MoveToCampaignModal from "./modals/MoveToCampaignModal";
import CampaignsAnnouncementModal from "./modals/CampaignsAnnouncementModal";
import AddToSuppressionListModal from "./modals/AddToSuppressionListModal";
import SuppressionListFormModal from "./modals/SuppressionListFormModal";

export enum ModalActionType {
    CONFIRMATION_MODAL = "CONFIRMATION_MODAL",
    DESTRUCTIVE_MODAL = "DESTRUCTIVE_MODAL",
    MESSAGE_MODAL = "MESSAGE_MODAL",
    MODAL_SAMPLE = "MODAL_SAMPLE",
    MODAL_CANCEL_FLOW = "MODAL_CANCEL_FLOW",
    MODAL_PAUSE_FLOW = "MODAL_PAUSE_FLOW",
    PAYMENT_CONFIRM_MODAL = "PAYMENT_CONFIRM_MODAL",
    CUSTOM_COLOR_MODAL = "CUSTOM_COLOR_MODAL",
    SYNC_CONNECTIONS_MODAL = "SYNC_CONNECTIONS_MODAL",
    DISCONNECT_CONNECTIONS_MODAL = "DISCONNECT_CONNECTIONS_MODAL",
    EXPORT_CONNECTIONS_MODAL = "EXPORT_CONNECTIONS_MODAL",
    EXPORT_TO_HUBSPOT_MODAL = "EXPORT_TO_HUBSPOT_MODAL",
    CONNECTIONS_OVERVIEW_MODAL = "CONNECTIONS_OVERVIEW_MODAL",
    CSV_OVERVIEW_MODAL = "CSV_OVERVIEW_MODAL",
    SCHEDULE_DISCONNECT_MODAL = "SCHEDULE_DISCONNECT_MODAL",
    CALCULATE_ENRICHMENT_MODAL = "CALCULATE_ENRICHMENT_MODAL",
    ENRICHMENT_COMPLETE_MODAL = "ENRICHMENT_COMPLETE_MODAL",
    REMOVAL_OVERVIEW_MODAL = "REMOVAL_OVERVIEW_MODAL",
    ADD_MEMBERS_MODAL = "ADD_MEMBERS_MODAL",
    COMPANY_OVERVIEW_MODAL = "COMPANY_OVERVIEW_MODAL",
    BULK_EDIT_TAGS_MODAL = "BULK_EDIT_TAGS_MODAL",
    CREATE_FEED_MODAL = "CREATE_FEED_MODAL",
    CREATE_FEED_GUIDE_MODAL = "CREATE_FEED_GUIDE_MODAL",
    RENAME_FEED_MODAL = "RENAME_FEED_MODAL",
    CHOOSE_TEMPLATE_MODAL = "CHOOSE_TEMPLATE_MODAL",
    CREATE_CUSTOM_FIELD_MODAL = "CREATE_CUSTOM_FIELD_MODAL",
    HUBSPOT_EXPORT_RESULT_MODAL = "HUBSPOT_EXPORT_RESULT_MODAL",
    NOT_FOUND_URLS_MODAL = "NOT_FOUND_URLS_MODAL",
    EDIT_KANBAN_MODAL = "EDIT_KANBAN_MODAL",
    ADD_TO_KANBAN_MODAL = "ADD_TO_KANBAN_MODAL",
    ADD_TO_CAMPAIGN_MODAL = "ADD_TO_CAMPAIGN_MODAL",
    MOVE_TO_CAMPAIGN_MODAL = "MOVE_TO_CAMPAIGN_MODAL",
    CAMPAIGNS_ANNOUNCEMENT_MODAL = "CAMPAIGNS_ANNOUNCEMENT_MODAL",
    ADD_TO_SUPPRESSION_LIST_MODAL = "ADD_TO_SUPPRESSION_LIST_MODAL",
    SUPPRESSION_LIST_FORM_MODAL = "SUPPRESSION_LIST_FORM_MODAL",
}

const AppModal: FunctionComponent = () => {
    const { store: { modal } } = useAppStore()
    const { hideModal } = useAppHandlers()

    const isMobile = useMediaQuery("(max-width: 768px)");

    if (!modal) return null

    let modalToShow;
    let modalWidth = "448px";

    switch (modal.type) {
        case ModalActionType.CONFIRMATION_MODAL:
            modalToShow = <ConfirmationModal />
            break
        case ModalActionType.DESTRUCTIVE_MODAL:
            modalToShow = <DestructiveModal />
            break
        case ModalActionType.MESSAGE_MODAL:
            modalToShow = <MessageModal />
            break
        case ModalActionType.MODAL_SAMPLE:
            modalToShow = <ModalSample />
            break
        case ModalActionType.MODAL_CANCEL_FLOW:
            modalToShow = <CancelFlow />
            break
        case ModalActionType.MODAL_PAUSE_FLOW:
            modalToShow = <PauseFlow />
            break
        case ModalActionType.PAYMENT_CONFIRM_MODAL:
            modalToShow = <PaymentConfirmedModal />
            break
        case ModalActionType.CUSTOM_COLOR_MODAL:
            modalToShow = <CustomColorModal {...modal.props}/>
            break
        case ModalActionType.SYNC_CONNECTIONS_MODAL:
            modalToShow = <SyncConnectionsModal {...modal.props} />
            modalWidth = "480px"
            break
        case ModalActionType.DISCONNECT_CONNECTIONS_MODAL:
            modalToShow = <DisconnectConnectionsModal {...modal.props} />
            break
        case ModalActionType.EXPORT_CONNECTIONS_MODAL:
            modalToShow = <ExportConnectionsModal {...modal.props} />
            break
        case ModalActionType.EXPORT_TO_HUBSPOT_MODAL:
            modalToShow = <ExportToHubspotModal {...modal.props} />
            break
        case ModalActionType.CONNECTIONS_OVERVIEW_MODAL:
            modalToShow = <ConnectionsOverviewModal {...modal.props} />
            break
        case ModalActionType.CSV_OVERVIEW_MODAL:
            modalToShow = <CsvOverviewModal {...modal.props} />
            break
        case ModalActionType.SCHEDULE_DISCONNECT_MODAL:
            modalToShow = <ScheduleDisconnectModal {...modal.props} />
            isMobile ? modalWidth = '' : modalWidth = '520px'
            break
        case ModalActionType.CALCULATE_ENRICHMENT_MODAL:
            modalToShow = <CalculateEnrichmentModal {...modal.props} />
            isMobile ? modalWidth = '' : modalWidth = '460px'
            break
        case ModalActionType.ENRICHMENT_COMPLETE_MODAL:
            modalToShow = <EnrichmentCompleteModal {...modal.props} />
            isMobile ? modalWidth = '' : modalWidth = '460px'
            break
        case ModalActionType.HUBSPOT_EXPORT_RESULT_MODAL:
            modalToShow = <HubspotExportResultModal {...modal.props} />
            isMobile ? modalWidth = '' : modalWidth = '448px'
            break
        case ModalActionType.REMOVAL_OVERVIEW_MODAL:
            modalToShow = <RemovalOverviewModal />
            break
        case ModalActionType.ADD_MEMBERS_MODAL:
            modalToShow = <AddMembersModal />
            modalWidth = '480px';
            break
        case ModalActionType.COMPANY_OVERVIEW_MODAL:
            modalToShow = <CompanyOverviewModal {...modal.props} />
            break
        case ModalActionType.BULK_EDIT_TAGS_MODAL:
            modalToShow = <BulkEditTagsModal {...modal.props} />
            isMobile ? modalWidth = '' : modalWidth = '448px'
            break
        case ModalActionType.CHOOSE_TEMPLATE_MODAL:
            modalToShow = <ChooseTemplateModal {...modal.props} />
            isMobile ? modalWidth = '' : modalWidth = '448px'
            break
        case ModalActionType.CREATE_FEED_MODAL:
            modalToShow = <CreateFeedModal {...modal.props} />
            isMobile ? modalWidth = '' : modalWidth = '480px'
            break
        case ModalActionType.CREATE_FEED_GUIDE_MODAL:
            modalToShow = <CreateFeedGuideModal {...modal.props} />
            isMobile ? modalWidth = '' : modalWidth = '793px'
            break
        case ModalActionType.RENAME_FEED_MODAL:
            modalToShow = <RenameFeedModal {...modal.props} />
            isMobile ? modalWidth = '' : modalWidth = '400px'
            break
        case ModalActionType.CREATE_CUSTOM_FIELD_MODAL:
            modalToShow = <CreateCustomFieldModal {...modal.props} />
            isMobile ? modalWidth = '' : modalWidth = '550px'
            break
        case ModalActionType.NOT_FOUND_URLS_MODAL:
            modalToShow = <NotFoundUrlsModal {...modal.props} />
            isMobile ? modalWidth = '' : modalWidth = '480px'
            break
        case ModalActionType.EDIT_KANBAN_MODAL:
            modalToShow = <EditKanbanModal {...modal.props} />
            isMobile ? modalWidth = '' : modalWidth = '480px'
            break
        case ModalActionType.ADD_TO_KANBAN_MODAL:
            modalToShow = <AddToKanbanModal {...modal.props} />
            isMobile ? modalWidth = '' : modalWidth = '480px'
            break
        case ModalActionType.ADD_TO_CAMPAIGN_MODAL:
            modalToShow = <AddToCampaignModal {...modal.props} />
            isMobile ? modalWidth = '' : modalWidth = '480px'
            break
        case ModalActionType.MOVE_TO_CAMPAIGN_MODAL:
            modalToShow = <MoveToCampaignModal {...modal.props} />
            isMobile ? modalWidth = '' : modalWidth = '480px'
            break
        case ModalActionType.CAMPAIGNS_ANNOUNCEMENT_MODAL:
            modalToShow = <CampaignsAnnouncementModal {...modal.props} />
            isMobile ? modalWidth = '' : modalWidth = '580px'
            break
        case ModalActionType.ADD_TO_SUPPRESSION_LIST_MODAL:
            modalToShow = <AddToSuppressionListModal {...modal.props} />
            isMobile ? modalWidth = '' : modalWidth = '560px'
            break
        case ModalActionType.SUPPRESSION_LIST_FORM_MODAL:
            modalToShow = <SuppressionListFormModal {...modal.props} />
            isMobile ? modalWidth = '' : modalWidth = '520px'
            break
    }

    // Special case for CreateFeedGuideModal - render directly without StyledModal wrapper
    if (modal?.type === ModalActionType.CREATE_FEED_GUIDE_MODAL) {
        return modal ? modalToShow || null : null;
    }

    // Allow backdrop click to close only for CreateCustomFieldModal and Inbox-opened BulkEditTagsModal
    const allowBackdropClose =
        modal?.type === ModalActionType.CREATE_CUSTOM_FIELD_MODAL ||
        (modal?.type === ModalActionType.BULK_EDIT_TAGS_MODAL && modal?.props?.closeOnBackdropClick === true);

    const handleHide = () => {
        if (
            modal?.type === ModalActionType.BULK_EDIT_TAGS_MODAL &&
            modal?.props?.closeOnBackdropClick === true &&
            isBulkEditTagsBackdropCloseBlocked()
        ) {
            return;
        }
        hideModal();
    };

    return (
        <StyledModal
            show={modal}
            onHide={handleHide}
            width={modalWidth}
            backdrop={allowBackdropClose ? true : "static"}
        >
            {modalToShow}
        </StyledModal>
    )
}

export default AppModal